import { zodResolver } from '@hookform/resolvers/zod';
import type { CustomerBusinessStatus } from '@plato/data/starrocks/types';
import { lexicalToPlainText } from '@plato/node-util/rich-text/lexical-to-plain-text';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Input } from '@plato/ui-legacy/components/ui/input';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import Text from '@plato/ui-legacy/components/ui/text';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { plainTextToLexicalSync } from '@plato/ui-legacy/lib/rich-text/plain-text-to-lexical';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import { Dialog } from '@plato/ui/containers/dialog';
import { useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { crmCustomerUpdateBusinessStatusInputSchema } from '../../../server/dto/customer-dto.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

type Bullet = {
    text: string;
    tooltip?: string;
};

type Reason = {
    value: string;
    label: string;
};

type ModeCopy = {
    title: string;
    action: string;
    actionVariant: 'primary' | 'danger';
    reasonLabel: string;
    reasonOtherPlaceholder: string;
    bullets: Bullet[];
    reasons: Reason[];
};

type MarkCustomerDialogProps = {
    customerId: string;
    mode: CustomerBusinessStatus;
};

const formSchema = crmCustomerUpdateBusinessStatusInputSchema.pick({ reason: true });
type FormValues = z.infer<typeof formSchema>;

export type UpdateCustomerBusinessStatusDialogProps = {
    customerId: string;
    mode: CustomerBusinessStatus;
};

function UpdateCustomerBusinessStatusDialogInternal({
    customerId,
    mode,
}: UpdateCustomerBusinessStatusDialogProps): ReactNode {
    const { t } = useTranslation('customer-detail');
    const { visible, remove } = useDialog();
    const { toast } = useToast();
    const apiUtils = api.useUtils();
    const [selectedReason, setSelectedReason] = useState<string | null>(null);
    const [otherText, setOtherText] = useState('');
    const sendAnalyticsEvent = useAnalytics();

    const config: ModeCopy =
        mode === 'active'
            ? {
                  title: t('mark_active_dialog__title'),
                  action: t('action__mark_customer_as_active'),
                  actionVariant: 'primary',
                  reasonLabel: t('mark_active_dialog__reason_label'),
                  reasonOtherPlaceholder: t('mark_active_dialog__reason_other_placeholder'),
                  bullets: [
                      { text: t('mark_active_dialog__bullet_recommendations') },
                      {
                          text: t('mark_active_dialog__bullet_plato_only'),
                          tooltip: t('mark_active_dialog__bullet_plato_only_tooltip'),
                      },
                  ],
                  reasons: [
                      {
                          value: 'resumed_operations',
                          label: t('mark_active_dialog__reason_resumed_operations'),
                      },
                      { value: 'other', label: t('mark_active_dialog__reason_other') },
                  ],
              }
            : {
                  title: t('mark_inactive_dialog__title'),
                  action: t('action__mark_customer_as_inactive'),
                  actionVariant: 'danger',
                  reasonLabel: t('mark_inactive_dialog__reason_label'),
                  reasonOtherPlaceholder: t('mark_inactive_dialog__reason_other_placeholder'),
                  bullets: [
                      {
                          text: t('mark_inactive_dialog__bullet_recommendations'),
                          tooltip: t('mark_inactive_dialog__bullet_recommendations_tooltip'),
                      },
                      {
                          text: t('mark_inactive_dialog__bullet_plato_only'),
                          tooltip: t('mark_inactive_dialog__bullet_plato_only_tooltip'),
                      },
                      { text: t('mark_inactive_dialog__bullet_reversible') },
                  ],
                  reasons: [
                      { value: 'business_closed', label: t('mark_inactive_dialog__reason_business_closed') },
                      { value: 'merged_accounts', label: t('mark_inactive_dialog__reason_merged_accounts') },
                      { value: 'other', label: t('mark_inactive_dialog__reason_other') },
                  ],
              };

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: { reason: null },
    });

    const { mutate: updateStatus, isPending } =
        api.platoUser.command.customers.updateBusinessStatus.useMutation({
            onError: () => {
                toast({ icon: 'warning', title: t('save_error') });
            },
            onSuccess: async (_, variables) => {
                await Promise.all([
                    apiUtils.platoUser.getDetailCustomer.getBasics.invalidate({
                        erpCustomerId: customerId,
                    }),
                    apiUtils.platoUser.getDetailCustomer.getCrmDetail.invalidate({
                        erpCustomerId: customerId,
                    }),
                    apiUtils.platoUser.getDetailCustomer.getLatestStatusChange.invalidate({
                        erpCustomerId: customerId,
                    }),
                    apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate(),

                    apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate(),
                ]);

                const isNowActive = variables.businessStatus === 'active';

                const reason = variables.reason ? lexicalToPlainText(variables.reason) : null;

                sendAnalyticsEvent(isNowActive ? 'customer_reactivated' : 'customer_marked_inactive', {
                    customer_id: customerId,
                    reason,
                });
            },
        });

    const onSubmit = form.handleSubmit((values) => {
        updateStatus(
            {
                customerId,
                businessStatus: mode,
                reason: values.reason ?? undefined,
            },
            {
                onSuccess: () => {
                    const isNowActive = mode === 'active';

                    const toastTitle = t(
                        isNowActive
                            ? 'mark_active_dialog__success_toast_title'
                            : 'mark_inactive_dialog__success_toast_title',
                    );

                    const toastDescription = t(
                        isNowActive
                            ? 'mark_active_dialog__success_toast_description'
                            : 'mark_inactive_dialog__success_toast_description',
                    );

                    const successToast = toast({
                        icon: 'success',
                        title: toastTitle,
                        description: (
                            <div className="flex gap-1">
                                <Text>{toastDescription}</Text>
                                <LinkText
                                    as="button"
                                    onClick={() => {
                                        successToast.dismiss();
                                        updateStatus({
                                            customerId,
                                            businessStatus: isNowActive ? 'inactive' : 'active',
                                            reason: plainTextToLexicalSync('rollback'),
                                        });
                                    }}
                                >
                                    {t('form__undo', { ns: 'common' })}
                                </LinkText>
                            </div>
                        ),
                    });

                    remove();
                },
            },
        );
    });

    return (
        <Dialog open={visible} onClose={remove}>
            <DialogForm className="sm:max-w-md">
                <DialogForm.Header title={config.title} />
                <DialogForm.Content className="gap-5 py-4">
                    <ul className="flex flex-col gap-1">
                        {config.bullets.map((bullet) => (
                            <li key={bullet.text} className="flex items-center">
                                <span className="h-1 w-1 shrink-0 rounded-full bg-neutral-600 mr-2" />
                                <Text className="mr-1">{bullet.text}</Text>
                                {bullet.tooltip && (
                                    <InfoHint>
                                        <InfoHintTrigger />
                                        <InfoHintContent>
                                            <InfoHintHeader>
                                                <InfoHintTitle />
                                            </InfoHintHeader>
                                            <InfoHintBody>{bullet.tooltip}</InfoHintBody>
                                        </InfoHintContent>
                                    </InfoHint>
                                )}
                            </li>
                        ))}
                    </ul>
                    <div className="flex flex-col gap-3">
                        <Text weight="semibold" color="neutral-900">
                            {config.reasonLabel}
                        </Text>
                        <Controller
                            control={form.control}
                            name="reason"
                            render={({ field }) => (
                                <>
                                    <div className="flex flex-wrap gap-2">
                                        {config.reasons.map((r) => {
                                            const isSelected = selectedReason === r.value;
                                            return (
                                                <Button
                                                    key={r.value}
                                                    type="button"
                                                    variant="quick-filter"
                                                    selected={isSelected}
                                                    className="h-10 px-5"
                                                    onClick={() => {
                                                        if (isSelected) {
                                                            setSelectedReason(null);
                                                            setOtherText('');
                                                            field.onChange(null);
                                                            return;
                                                        }
                                                        setSelectedReason(r.value);
                                                        if (r.value === 'other') {
                                                            field.onChange(plainTextToLexicalSync(otherText));
                                                        } else {
                                                            setOtherText('');
                                                            field.onChange(plainTextToLexicalSync(r.label));
                                                        }
                                                    }}
                                                >
                                                    {r.label}
                                                </Button>
                                            );
                                        })}
                                    </div>
                                    {selectedReason === 'other' && (
                                        <Input
                                            placeholder={config.reasonOtherPlaceholder}
                                            value={otherText}
                                            onChange={(e) => {
                                                setOtherText(e.target.value);
                                                field.onChange(plainTextToLexicalSync(e.target.value));
                                            }}
                                        />
                                    )}
                                </>
                            )}
                        />
                    </div>
                </DialogForm.Content>
                <DialogForm.SubmitBlock
                    onCancel={remove}
                    onSubmit={() => void onSubmit()}
                    isLoading={isPending}
                    submitText={config.action}
                    variant={config.actionVariant}
                />
            </DialogForm>
        </Dialog>
    );
}

export const UpdateCustomerBusinessStatusDialog = DialogStateManager.create<MarkCustomerDialogProps>(
    ({ customerId, mode }: MarkCustomerDialogProps): ReactNode => (
        <UpdateCustomerBusinessStatusDialogInternal customerId={customerId} mode={mode} />
    ),
);
